import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import apiConfig from "../../config/apiConfig";
import { FiMessageSquare } from "react-icons/fi";

export default function Payment() {
    const location = useLocation();
    const paymentSession = location.state?.paymentSession;
    const currency = location.state?.currency || 'USD';
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        if (paymentSession) {
            const initializeHostedCheckout = () => {
                setError(null);
                setIsLoading(true);
                const script = document.createElement('script');
                script.src = apiConfig.checkout.hostedCheckoutEndpoint;
                script.async = true;
                script.onload = () => {
                    const HostedCheckoutSDK = window.HostedCheckoutSDK || window.exports?.HostedCheckoutSDK;
                    if (!HostedCheckoutSDK) {
                        setError('Failed to load payment SDK.');
                        setIsLoading(false);
                        return;
                    }
                    const hostedCheckoutSdk = new HostedCheckoutSDK();
                    hostedCheckoutSdk.initialize({
                        containerId: 'hosted-checkout',
                        sessionId: paymentSession.sessionId,
                        environment: 'uat',
                        transactionRequestMode: 'PERFORM_TRANSACTION',
                        locale: 'en_US',
                        onBackToCart: () => {
                            navigate('/shoppingcart');
                        },
                        onStartInitialization: () => {
                            setIsLoading(true);
                        },
                        onEndInitialization: () => {
                            setIsLoading(false);
                        },
                        onPaySubmit: () => {
                            console.log('Payment submitted');
                            return Promise.resolve('SUCCESS');
                        },
                        onApprove: (response) => {
                            const transactionData = {
                                ...response.data,
                                successful: true,
                                approvedAmount: response.data.approvedAmount || '0.00',
                                totalAmount: response.data.totalAmount || '0.00'
                            };
                            navigate('/checkoutresult', { state: { transactionData, currency } });
                        },
                        onError: (error) => {
                            const transactionData = {
                                ...error.data,
                                successful: false,
                                approvedAmount: error.data.approvedAmount || '0.00',
                                totalAmount: error.data.totalAmount || '0.00',
                                status: error.data.status || 'Transaction Failed'
                            };
                            navigate('/checkoutresult', { state: { transactionData, currency } });
                        },
                        onSessionError: (response) => {
                            console.log(JSON.stringify(response));
                            setError('No valid payment session available.');
                            setIsLoading(false);
                        },
                        onGooglePayAuthorized: (response) => {
                            const transactionData = {
                                ...response.data,
                                successful: true,
                                approvedAmount: response.data.approvedAmount || '0.00',
                                totalAmount: response.data.totalAmount || '0.00'
                            };
                            navigate('/checkoutresult', { state: { transactionData, currency } });
                        },
                        onGooglePayCanceled: () => {},
                        onGooglePayError: (response) => {
                            console.log(JSON.stringify(response));
                            setError('Unable to process the GooglePay payment.');
                            setIsLoading(false);
                        },
                        onPaypalCaptured: (response) => {
                            const transactionData = {
                                ...response.data,
                                successful: true,
                                approvedAmount: response.data.approvedAmount || '0.00',
                                totalAmount: response.data.totalAmount || '0.00',
                                paymentMethod: 'PayPal'
                            };
                            navigate('/checkoutresult', { state: { transactionData, currency } });
                        },
                        onPaypalCanceled: () => {
                            setError('PayPal payment was canceled. Please try again.');
                            setIsLoading(false);
                        },
                        onPaypalError: (error) => {
                            console.error('PayPal Error:', error);
                            if (error?.message?.includes('order id') || error?.err?.includes('order id')) {
                                setError('PayPal configuration error. Please contact support.');
                                setIsLoading(false);
                                return;
                            }
                            const transactionData = {
                                ...error.data,
                                successful: false,
                                approvedAmount: '0.00',
                                totalAmount: error.data?.totalAmount || '0.00',
                                status: 'PayPal Transaction Failed',
                                paymentMethod: 'PayPal'
                            };
                            navigate('/checkoutresult', { state: { transactionData, currency } });
                        },
                        onApplePayAuthorized: (response) => {
                            const transactionData = {
                                ...response.data,
                                successful: true,
                                approvedAmount: response.data.approvedAmount || '0.00',
                                totalAmount: response.data.totalAmount || '0.00',
                                paymentMethod: 'Apple Pay'
                            };
                            navigate('/checkoutresult', { state: { transactionData, currency } });
                        },
                        onApplePayCanceled: () => {
                            setError('Apple Pay payment was canceled. Please try again.');
                            setIsLoading(false);
                        },
                        onApplePayError: (error) => {
                            console.log('Apple Pay Error:', JSON.stringify(error));
                            setError('Unable to process the Apple Pay payment.');
                            setIsLoading(false);
                        }
                    });
                    setTimeout(() => { setIsLoading(false); }, 3000);
                };
                script.onerror = () => {
                    setError('Failed to load payment form.');
                    setIsLoading(false);
                };
                document.head.appendChild(script);
            };
            initializeHostedCheckout();
        } else {
            setError('No valid payment session available.');
            setIsLoading(false);
        }
    }, [paymentSession, navigate]);

    if (!paymentSession) {
        return (
            <div className="text-center py-8">
                <p className="text-red-600 mb-4">Unable to load payment session. Please go back and try again.</p>
                <button onClick={() => navigate('/shoppingcart')} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors">
                    Back to Cart
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-[400px]">
            {isLoading && (
                <div className="flex flex-col items-center justify-center py-12">
                    <div className="inline-flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                        <FiMessageSquare className="w-4 h-4 mr-2" />
                        Secure Payment Processing
                    </div>
                    <div className="flex justify-center items-center">
                        <div className="spinner" role="status">
                            <span className="loadingText">Loading secure payment form...</span>
                        </div>
                    </div>
                </div>
            )}
            <div id="hosted-checkout" className={isLoading ? 'opacity-0' : 'opacity-100 transition-opacity duration-300'}></div>
        </div>
    );
}